/**
 * Knowledge Graph tools for the VoiceChat component
 * 
 * This file contains tools for interacting with the Knowledge Graph:
 * - AddEntityTool: Add a new entity to the graph
 * - AddRelationTool: Add a relationship between entities
 * - QueryEntitiesTools: Query entities based on various criteria
 */
import { toolRegistry, createMessage, type OpenAIToolSchema, ToolName } from './ToolRegistry';
import type { ToolContext, Message } from './ToolRegistry';
import { useKnowledgeGraphStore, EntityType, type Entity, type Relation } from '~/stores/knowledgeGraphStore';

// Type for the knowledge graph store instance
type KnowledgeGraphStore = ReturnType<typeof useKnowledgeGraphStore>;

/**
 * Interface for entity input arguments
 */
export interface AddEntityArgs {
  name: string;
  entityType: string;
  attributes?: Record<string, any>;
}

/**
 * Interface for relation input arguments
 */
export interface AddRelationArgs {
  subjectId: string;
  predicate: string;
  objectId: string;
}

/**
 * Interface for entity query arguments
 */
export interface QueryEntitiesArgs {
  entityType?: string;
  searchTerm?: string;
}

// --------------------------------------
// OpenAI Tool Schemas
// --------------------------------------

/**
 * OpenAI schema for queryKnowledgeGraph tool
 */
const queryKnowledgeGraphSchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.QUERY_KNOWLEDGE_GRAPH,
  description: 'Search for entities in the knowledge graph by type and search term',
  parameters: {
    type: 'object',
    properties: {
      entityType: {
        type: 'string',
        description: 'Type of entity to search for',
        enum: ['person', 'place', 'object', 'any']
      },
      searchTerm: {
        type: 'string',
        description: 'Text to search for within entity names and attributes'
      }
    },
    required: ['entityType', 'searchTerm']
  }
};

/**
 * OpenAI schema for addEntity tool
 */
const addEntitySchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.ADD_KNOWLEDGE_GRAPH_ENTITY,
  description: 'Add a new entity to the knowledge graph (person, place, or object)',
  parameters: {
    type: 'object',
    properties: {
      entityType: {
        type: 'string',
        description: 'Type of entity to add',
        enum: ['person', 'place', 'object']
      },
      name: {
        type: 'string',
        description: 'Name of the entity'
      },
      attributes: {
        type: 'object',
        description: 'Additional attributes for the entity (e.g., age, occupation, address)'
      }
    },
    required: ['entityType', 'name']
  }
};

/**
 * OpenAI schema for addRelation tool
 */
const addRelationSchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.ADD_KNOWLEDGE_GRAPH_RELATION,
  description: 'Create a relationship between two entities in the knowledge graph',
  parameters: {
    type: 'object',
    properties: {
      subjectId: {
        type: 'string',
        description: 'ID of the subject entity (e.g., a person)'
      },
      predicate: {
        type: 'string',
        description: 'Type of relationship (e.g., "works at", "lives in", "is friends with")'
      },
      objectId: {
        type: 'string',
        description: 'ID of the object entity (e.g., a place)'
      }
    },
    required: ['subjectId', 'predicate', 'objectId']
  }
};

/**
 * OpenAI schema for getAllEntities tool
 */
const getAllEntitiesSchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.GET_KNOWLEDGE_GRAPH_ENTITIES,
  description: 'Retrieve all entities of a given type from the knowledge graph',
  parameters: {
    type: 'object',
    properties: {
      entityType: {
        type: 'string',
        description: 'Type of entities to retrieve (optional, returns all if omitted)',
        enum: ['person', 'place', 'object']
      }
    },
    required: []
  }
};

/**
 * OpenAI schema for queryEventEntities tool
 */
// const queryEventEntitiesSchema: OpenAIToolSchema = {
//   type: 'function',
//   name: ToolName.GET_KNOWLEDGE_GRAPH_RELATION_ENTITIES,
//   description: 'Get relationships between calendar events and entities',
//   parameters: {
//     type: 'object',
//     properties: {
//       eventId: {
//         type: 'string',
//         description: 'Calendar event ID to get related entities'
//       },
//       entityId: {
//         type: 'string',
//         description: 'Entity ID to get related events'
//       },
//       relationshipType: {
//         type: 'string',
//         description: 'Filter by relationship type (e.g., "organizer", "attendee", "location")'
//       }
//     },
//     required: []
//   }
// };

/**
 * Create a formatted message for when an entity is added
 */
export function createEntityAddedMessage(entity: Entity): Message {
  return createMessage(`I've added ${entity.name} to my memory as a ${entity.type}.`);
}

/**
 * Create a formatted message for when a relation is added
 */
export function createRelationAddedMessage(relation: Relation, fromEntityName: string, toEntityName: string): Message {
  return createMessage(`I've noted that ${fromEntityName} ${relation.predicate} ${toEntityName}.`);
}

/**
 * Create a formatted message for entity query results
 */
export function createEntityQueryResultMessage(entities: Entity[], entityType?: string): Message {
  let messageText = '';
  
  if (entities.length === 0) {
    messageText = `I don't have any ${entityType || ''} entities stored in my memory yet.`;
  } else if (entities.length === 1) {
    const entity = entities[0];
    messageText = `I found 1 entity: ${entity.name} (${entity.type})`;
  } else {
    messageText = `I found ${entities.length} entities:`;
    const entityTypes = new Set(entities.map(e => e.type));
    if (entityTypes.size === 1) {
      messageText += ` ${entities.map(e => e.name).join(', ')}`;
    } else {
      // If multiple types, group by type
      messageText += entities.reduce((acc, entity) => {
        return acc + `\n- ${entity.name} (${entity.type})`;
      }, '');
    }
  }
  
  return createMessage(messageText);
}

/**
 * Register all knowledge graph tools with the registry and configure them with the knowledge graph store
 * Similar approach to how calendar tools are registered and configured
 * 
 * @param knowledgeGraphStore The knowledge graph store to use for knowledge graph operations
 * @returns void - No API needed as configuration is done directly
 */
export function registerKnowledgeGraphTools(knowledgeGraphStore: KnowledgeGraphStore): void {
  // Validate the knowledge graph store
  if (!knowledgeGraphStore) {
    console.error('Knowledge graph store is required to register knowledge graph tools');
    throw new Error('Knowledge graph store is required to register knowledge graph tools');
  }
  
  // Helper to ensure store is initialized
  const ensureStore = async () => {
    if (!knowledgeGraphStore.ready) {
      await knowledgeGraphStore.initialize();
    }
    
    return knowledgeGraphStore;
  };
  
  // Register addEntity tool
  toolRegistry.register({
    name: ToolName.ADD_KNOWLEDGE_GRAPH_ENTITY,
    openAISchema: addEntitySchema,
    execute: async (args: AddEntityArgs, context: ToolContext) => {
      // Ensure store is initialized
      const store = await ensureStore();
      
      // Convert string type to enum
      const entityType = 
        args.entityType === 'person' ? EntityType.PERSON :
        args.entityType === 'place' ? EntityType.PLACE :
        args.entityType === 'object' ? EntityType.OBJECT : undefined;
      
      if (!entityType) {
        throw new Error(`Invalid entity type: ${args.entityType}`);
      }
      
      // Validate required parameters
      if (!args.name || args.name.trim() === '') {
        throw new Error('Entity name is required');
      }
      
      // Add the entity
      const entity = await store.addEntity(
        entityType,
        args.name,
        args.attributes || {}
      );
      
      context.logger.info(`Knowledge Graph: Added entity with ID ${entity.id}`);
      
      // Add message to conversation
      context.conversation.push(createEntityAddedMessage(entity));
      
      return entity;
    },
    formatResult: (entity: Entity) => createEntityAddedMessage(entity),
    getContinuationInstructions: () => {
      return "Acknowledge the entity was added and ask if the user would like to add any relationships for this entity.";
    }
  });
  
  // Register addRelation tool
  toolRegistry.register({
    name: ToolName.ADD_KNOWLEDGE_GRAPH_RELATION,
    openAISchema: addRelationSchema,
    execute: async (args: AddRelationArgs, context: ToolContext) => {
      // Ensure store is initialized
      const store = await ensureStore();
      
      // Validate required parameters
      if (!args.subjectId) {
        throw new Error('Subject entity ID is required');
      }
      if (!args.predicate || args.predicate.trim() === '') {
        throw new Error('Relation predicate is required');
      }
      if (!args.objectId) {
        throw new Error('Object entity ID is required');
      }
      
      // Add the relation
      const relation = await store.addRelation(
        args.subjectId,
        args.predicate,
        args.objectId
      );
      
      context.logger.info(`Knowledge Graph: Added relation from ${args.subjectId} to ${args.objectId} with predicate "${args.predicate}"`);
      
      // Get entity names for the message
      const fromEntity = store.getEntity(relation.subject);
      const toEntity = store.getEntity(relation.object);
      
      if (fromEntity && toEntity) {
        context.conversation.push(createRelationAddedMessage(relation, fromEntity.name, toEntity.name));
      }
      
      return relation;
    },
    getContinuationInstructions: () => {
      return "Acknowledge the relationship was added and ask if the user would like to add more information to the knowledge graph.";
    }
  });
  
  // Register queryKnowledgeGraph tool
  toolRegistry.register({
    name: ToolName.QUERY_KNOWLEDGE_GRAPH,
    openAISchema: queryKnowledgeGraphSchema,
    execute: async (args: QueryEntitiesArgs, context: ToolContext) => {
      // Ensure store is initialized
      const store = await ensureStore();
      
      let entityType: EntityType | undefined;
      
      // Convert string type to enum if provided
      if (args?.entityType) {
        entityType = 
          args.entityType === 'person' ? EntityType.PERSON :
          args.entityType === 'place' ? EntityType.PLACE :
          args.entityType === 'object' ? EntityType.OBJECT : undefined;
      }
      
      // Query entities
      let entities: Entity[];
      if (args?.searchTerm) {
        // If search term provided, use queryEntities
        entities = await store.queryEntities(entityType, args.searchTerm);
      } else {
        // Otherwise use getAllEntities
        entities = store.getAllEntities(entityType);
      }
      
      context.logger.info(`Knowledge Graph: Found ${entities.length} entities`);
      
      // Add message to conversation
      context.conversation.push(createEntityQueryResultMessage(entities, args.entityType));
      
      return entities;
    },
    formatResult: (entities: Entity[], args?: QueryEntitiesArgs) => {
      return createEntityQueryResultMessage(entities, args?.entityType);
    },
    getContinuationInstructions: () => {
      return "Describe the entities found and ask if the user would like more information about any specific entity.";
    }
  });
  
  // Also register getAllEntities as an alias to queryEntities
  toolRegistry.register({
    name: ToolName.GET_KNOWLEDGE_GRAPH_ENTITIES,
    openAISchema: getAllEntitiesSchema,
    execute: async (args: QueryEntitiesArgs, context: ToolContext) => {
      // Ensure store is initialized
      const store = await ensureStore();
      
      let entityType: EntityType | undefined;
      
      // Convert string type to enum if provided
      if (args?.entityType) {
        entityType = 
          args.entityType === 'person' ? EntityType.PERSON :
          args.entityType === 'place' ? EntityType.PLACE :
          args.entityType === 'object' ? EntityType.OBJECT : undefined;
      }
      
      // Query entities
      const entities = store.getAllEntities(entityType);
      
      context.logger.info(`Knowledge Graph: Found ${entities.length} entities`);
      
      // Add message to conversation
      context.conversation.push(createEntityQueryResultMessage(entities, args?.entityType));
      
      return entities;
    },
    formatResult: (entities: Entity[], args?: QueryEntitiesArgs) => {
      return createEntityQueryResultMessage(entities, args?.entityType);
    },
    getContinuationInstructions: () => {
      return "Describe the entities found and ask if the user would like more information about any specific entity.";
    }
  });
  
  // // Register queryEventEntities tool if implemented
  // if ('getEventEntities' in knowledgeGraphStore && 
  //     typeof (knowledgeGraphStore as any).getEventEntities === 'function') {
  //   toolRegistry.register({
  //     name: ToolName.GET_KNOWLEDGE_GRAPH_RELATION_ENTITIES,
  //     openAISchema: queryEventEntitiesSchema,
  //     execute: async (args: any, context: ToolContext) => {
  //       // Ensure store is initialized
  //       const store = await ensureStore();
        
  //       // Get relationships between events and entities
  //       // @ts-ignore - Using dynamic feature check to handle optional API
  //       const results = await store.getEventEntities(args.eventId, args.entityId, args.relationshipType);
        
  //       context.logger.info(`Knowledge Graph: Found ${results.length} event-entity relationships`);
        
  //       // Add message to conversation
  //       const messageText = `I found ${results.length} relationships between events and entities.`;
  //       context.conversation.push(createMessage(messageText));
        
  //       return results;
  //     },
  //     getContinuationInstructions: () => {
  //       return "Summarize the relationships between events and entities found and ask if the user would like more specific information.";
  //     }
  //   });
  // }
  
  console.info('Knowledge graph tools registered and configured with knowledge graph store');
} 